import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";
import { FaChevronDown, FaHeadset, FaComments, FaQuestionCircle } from "react-icons/fa";
import { crmFaqItems } from "../../data/crmPageContent";
import { CrmReveal } from "./CrmMotion";

export type FaqItem = {
    question: string;
    answer: string;
};

type CrmFaqSectionProps = {
    eyebrow?: string;
    title?: string;
    lead?: string;
    items?: FaqItem[];
    showEyebrow?: boolean;
    footerTitle?: string;
    footerLead?: string;
    footerButtonLabel?: string;
    footerButtonPath?: string;
};

export const CrmFaqSection = ({
    eyebrow = "FAQ",
    title = "Questions? We've Got Answers.",
    lead = "Learn how HonestiIQ helps teams move faster with smarter CRM workflows, analytics, and collaboration tools.",
    items = crmFaqItems,
    showEyebrow = true,
    footerTitle,
    footerLead,
    footerButtonLabel = "Contact Us",
    footerButtonPath = "/contact",
}: CrmFaqSectionProps) => {
    const [open, setOpen] = useState<number | null>(0);
    const navigate = useNavigate();

    return (
        <section className="crm-faq">
            <div className="crm-container">
                <div className="crm-faq__layout">
                    <CrmReveal className="crm-faq__intro">
                        {showEyebrow && <span className="crm-eyebrow">{eyebrow}</span>}
                        <h2 className="crm-h2">{title}</h2>
                        <div className="crm-section-accent crm-section-accent--center" />
                        <p className="crm-lead">{lead}</p>
                        <div className="crm-faq__actions">
                            <div className="crm-faq__visual">
                                <div className="crm-faq__visual-icon"><FaHeadset size={20} /></div>
                                <div className="crm-faq__visual-icon"><FaComments size={18} /></div>
                                <div className="crm-faq__visual-icon"><FaQuestionCircle size={18} /></div>
                            </div>
                            <Button className="crm-btn crm-btn--primary" onClick={() => navigate("/contact")}>
                                Contact Us <ArrowRightOutlined />
                            </Button>
                        </div>
                    </CrmReveal>

                    <div className="crm-faq__list">
                        {items.map((item, i) => {
                            const isOpen = open === i;
                            return (
                                <motion.div
                                    key={item.question}
                                    className={`crm-faq__item ${isOpen ? "crm-faq__item--open" : ""}`}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ delay: i * 0.05 }}
                                >
                                    <button
                                        type="button"
                                        className="crm-faq__trigger"
                                        onClick={() => setOpen(isOpen ? null : i)}
                                        aria-expanded={isOpen}
                                    >
                                        <span>{item.question}</span>
                                        <motion.span
                                            className="crm-faq__chevron"
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <FaChevronDown size={14} />
                                        </motion.span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                className="crm-faq__panel"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                            >
                                                <p>{item.answer}</p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </div>

                    {(footerTitle || footerLead) && (
                        <CrmReveal className="crm-faq__footer">
                            {footerTitle && <h3 className="crm-faq__footer-title">{footerTitle}</h3>}
                            {footerLead && <p className="crm-faq__footer-lead">{footerLead}</p>}
                            <Button className="crm-btn crm-btn--primary" onClick={() => navigate(footerButtonPath)}>
                                {footerButtonLabel} <ArrowRightOutlined />
                            </Button>
                        </CrmReveal>
                    )}
                </div>
            </div>
        </section>
    );
};
